import React, { useState, useEffect, useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useParams, useHistory } from "react-router-dom";

import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Container,
  IconButton,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@material-ui/core";
import { ArrowBack, RemoveCircleOutline } from "@material-ui/icons";

import { common, confirm, roles } from "../../messages";
import { storeLabels } from "../../messages/businessStores";
import { useBmapi } from "../../utils/bmapi-context";
import { useCampaigns } from "../../utils/campaigns";
import { MANAGER_ROUTES, USER_ROLES } from "../../utils/constants";
import { getErrorMessageString } from "../../utils/errors";
import styles from "../../utils/styles";
import Ribbon from "../../ui/Ribbon";
import Title from "../../ui/Title";
import BusinessSelectDialog from "../../ui/BusinessSelectDialog";
import Confirm from "../../ui/Confirm";

const useStyles = makeStyles({
  root: {
    width: "100%",
  },
  container: {
    maxHeight: 550,
  },
  redIcon: {
    color: "#da0e12",
  },
});

export const rolesLabels = {
  ASSISTANT: roles.storeAssistant,
  BUSINESS_MANAGER: roles.storeManager,
  CASHIER: roles.operator,
  EDITOR: roles.campaignManager,
  REPORTER: roles.viewer,
  SUBSCRIPTION_MANAGER: roles.subscriptionManager,
  SUBSCRIPTION_PLAN_MANAGER: roles.subscriptionPlanManager,
  SUBSCRIPTION_STAMP_MANAGER: roles.subscriptionStampManager,
};

const byName = (a, b) => a.name.localeCompare(b.name);

export const getPermName = (code, intl) =>
  intl.formatMessage(
    rolesLabels[Object.entries(USER_ROLES).find((r) => r[1] === code)[0]]
  );

function BusinessElement({ business, categories, gdspTenant, onDelete }) {
  const intl = useIntl();
  const tableClasses = useStyles();
  const [deleteRequest, setDeleteRequest] = useState(false);

  const categoriesMap = new Map(
    categories?.map((obj) => [obj.id, obj.str_value])
  );

  const category =
    categoriesMap.get(business.attrs?.find((obj) => obj.k === "category")?.v) ||
    "-";

  const province =
    business.attrs?.find((obj) => obj.k === "province")?.v || "-";

  const handleDelete = () => {
    setDeleteRequest(false);
    onDelete(business.id);
  };

  return (
    <React.Fragment>
      <Confirm
        open={!!deleteRequest}
        onConfirm={handleDelete}
        onCancel={() => setDeleteRequest(false)}
        text={intl.formatMessage(confirm.deleteElement)}
      />
      <TableRow>
        <TableCell padding="none">{business.name}</TableCell>
        <TableCell padding="none">{category}</TableCell>
        {gdspTenant && <TableCell padding="none">{province}</TableCell>}
        <TableCell padding="none" align="right">
          <IconButton
            aria-label="remove"
            className={tableClasses.redIcon}
            onClick={() => setDeleteRequest(true)}
          >
            <RemoveCircleOutline />
          </IconButton>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

export default function CampaignBusinesses() {
  const { campaignId } = useParams();
  const classes = styles.useStyles();
  const tableClasses = useStyles();
  const intl = useIntl();
  const { campaigns, loadCampaigns } = useCampaigns();
  const [campaignBusinesses, setCampaignBusinesses] = useState(false);
  const history = useHistory();
  const {
    bmapi,
    startLoading,
    stopLoading,
    notifyError,
    notifySuccess,
  } = useBmapi();
  const [campaign, setCampaign] = useState(null);
  const [bg, setBg] = useState(false);
  const [categories, setCategories] = useState(false);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [open, setOpen] = React.useState(false);

  const businessLabels = storeLabels;
  const gdspTenant = bmapi.settings.tenantId === "6294348160712791";

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const loadCategories = useCallback(() => {
    bmapi
      .getGenListCategories()
      .then((resp) => {
        if (!resp) {
          resp = [];
        }
        resp.items.sort((a, b) => a.str_value.localeCompare(b.str_value));

        setCategories(resp);
      })
      .catch((e) => {
        if (
          e.httpCode === 404 &&
          getErrorMessageString(e) === "Entity not found"
        ) {
          return;
        }
        notifyError(getErrorMessageString(e, intl));
      });
  }, [bmapi, intl, notifyError]);

  useEffect(() => {
    loadCampaigns();
    loadCategories();
  }, [loadCampaigns, loadCategories]);

  useEffect(() => {
    if (campaigns) {
      setCampaign(campaigns.find((cv) => cv.campaign_id === campaignId));
    }
  }, [campaignId, campaigns]);

  const update = useCallback(() => {
    if (campaign) {
      startLoading();

      setBg(campaign.image_url || bmapi.createBg(campaign.name));
      bmapi
        .getCampaignDetails(campaign.campaign_id)
        .catch((e) => notifyError(getErrorMessageString(e, intl)))
        .finally(stopLoading);

      bmapi.getCampaignBusinesses(campaign.campaign_id).then((businesses) => {
        setCampaignBusinesses(businesses);
      });
    }
  }, [bmapi, campaign, intl, notifyError, startLoading, stopLoading]);

  useEffect(() => {
    update();
  }, [update]);

  const goToCampaigns = () => {
    history.push(MANAGER_ROUTES.CAMPAIGNS.replace(":filter?/", ""));
  };

  const removeBusinessFromCampaign = (bs) => {
    startLoading();

    return bmapi
      .unJoinCampaign(campaign.id, bs)
      .then(() => {
        notifySuccess(
          intl.formatMessage(
            {
              id: "component.campaignUnjoin.successMessage",
              defaultMessage:
                "Hai sospeso con successo l'adesione alla campagna {name}",
            },
            {
              name: campaign.name,
            }
          )
        );

        update();
      })
      .catch((e) => notifyError(getErrorMessageString(e, intl)))
      .finally(stopLoading);
  };

  const addBusinessToCampaign = async (bs) => {
    try {
      await Promise.all(bs.map((id) => bmapi.signCampaign(id, campaignId)));

      notifySuccess(
        intl.formatMessage(
          {
            id: "component.campaignJoin.successMessage",
            defaultMessage: "Hai aderito con successo alla campagna {name}",
          },
          { name: campaign.name }
        )
      );

      update();
    } catch (e) {
      notifyError(getErrorMessageString(e, intl));
    } finally {
      stopLoading();
      handleClose();
    }
  };

  return (
    !!campaign && (
      <React.Fragment>
        <Container maxWidth="sm">
          <Box mb={4}>
            <Card style={{ position: "relative" }}>
              {campaign.demo && (
                <Ribbon label={intl.formatMessage(common.demo)} />
              )}
              {!!bg && <CardMedia className={classes.cardMedia} image={bg} />}
            </Card>
          </Box>
        </Container>

        <Container maxWidth="md">
          <Box my={2}>
            <Button onClick={goToCampaigns} startIcon={<ArrowBack />}>
              {intl.formatMessage(common.back)}
            </Button>
          </Box>

          <Title>Business Aderenti</Title>
          <Box mb={2}>
            {campaignBusinesses &&
              (!campaignBusinesses.length ? (
                <Card>
                  <CardContent>
                    <Typography gutterBottom>
                      <FormattedMessage
                        {...businessLabels.noBusinessRegistered}
                      />
                    </Typography>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent>
                    <TableContainer className={tableClasses.container}>
                      <Table
                        aria-label="sticky table"
                        size="small"
                        stickyHeader
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell>Nome</TableCell>
                            <TableCell>Categoria</TableCell>
                            {gdspTenant && <TableCell>Provincia</TableCell>}
                            <TableCell></TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {campaignBusinesses
                            .sort(byName)
                            .slice(
                              page * rowsPerPage,
                              page * rowsPerPage + rowsPerPage
                            )
                            .map((b) => {
                              return (
                                <BusinessElement
                                  key={b.id}
                                  business={b}
                                  categories={categories.items}
                                  gdspTenant={gdspTenant}
                                  onDelete={removeBusinessFromCampaign}
                                />
                              );
                            })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TablePagination
                      rowsPerPageOptions={[25, 50, 100]}
                      component="div"
                      count={campaignBusinesses.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onChangePage={handleChangePage}
                      onChangeRowsPerPage={handleChangeRowsPerPage}
                    />

                    <Box display="flex" justifyContent="flex-end" mt={2}>
                      <Button
                        color="primary"
                        variant="contained"
                        onClick={handleClickOpen}
                      >
                        <FormattedMessage
                          id="common.add"
                          defaultMessage="Aggiungi"
                        />
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              ))}
          </Box>

          <BusinessSelectDialog
            addBusinessToCampaign={addBusinessToCampaign}
            handleClose={handleClose}
            open={open}
            campaignBusinesses={campaignBusinesses || []}
          />

          <Box my={2}>
            <Button onClick={goToCampaigns} startIcon={<ArrowBack />}>
              {intl.formatMessage(common.back)}
            </Button>
          </Box>
        </Container>
      </React.Fragment>
    )
  );
}
